import React from "react"
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {LayersControl} from "react-leaflet";
import {useCurrentUser} from "../../auth/CurrentUserProvider";

import Page from "../Page";
import MDBox from "../../materialDashboard/components/MDBox";
import MDButton from "../../materialDashboard/components/MDButton";
import useDataTable from "../../components/DataTable/useDataTable";
import StationsTable from "./StationsTable";
import {useStationsQuery} from "./query";
import GeoJSONMap from "../../components/maps/GeoJSONMap";
import GeoJSONPointsCollection from "../../components/maps/GeoJSONMap/GeoJSONPointsCollection";
import {camelToSnakeCase} from "../../components/DataTable/useDataTableSorting";

import Card from "@mui/material/Card";
import {useAuth} from "../../auth/AuthProvider";
import useStationsMap from "./useStationsMap";
import renderStationTooltip from "../../components/maps/stationFeature/renderStationFeatureTooltip";
import Box from "@mui/material/Box";
import StationFeatureChip from "../../components/maps/stationFeature/StationFeatureChip";
import {ExpandButtonIcon, useExpandButtonIcon} from "../../components/maps/ExpandButtonIcon";
import ExportToolbar from "./ExportToolbar";


const Stations = () => {
    const {t} = useTranslation(['stations']);
    const navigate = useNavigate();
    const {currentUser} = useCurrentUser();
    const {workgroupAuthorization} = useAuth();

    const selectedWorkgroups = currentUser.data.role === "ADMIN" ? [] : currentUser.data.workgroups;
    const filterProps = { initialValues: { selectedWorkgroups } };
    const initialFiltersValue = { workgroup_id__in: selectedWorkgroups.map(w => w.id), currently_active: true }
    const {fetchDataOptions, ...tableState} = useDataTable({
        dataTableId: "stations",
        initialFiltersValue
    });

    const {pageSize, pageIndex, ...rest} = fetchDataOptions;
    const stationFilter = {
        ...rest,
        orderBy: tableState.sorting
            .map(({id, desc}) => id === "station" ? ({id: 'code', desc}) : ({id, desc}))  // when sorting by station use the code
            .map(({id, desc}) => `${desc ? "+" : "-"}${camelToSnakeCase(id)}`).join(",")
    }

    // ref is need to call flyTo and other methods on the map
    const map = useStationsMap({stationsFilters: stationFilter});
    const expandButtonState = useExpandButtonIcon();

    const stationTableFilter = {pageSize, pageIndex, ...stationFilter, id__in: map.selectedFeatureIds.join(",")}

    const dataQuery = useStationsQuery(stationTableFilter);

    return <Page path={"/stations"}>
        <MDBox px={2}>
            <Box justifyContent="space-between" alignItems="center" display="flex" flexWrap="wrap" gap={2} mb={2}>
                {
                    workgroupAuthorization.stations.create &&
                    <MDButton variant="gradient" color="info" onClick={() => navigate("/stations/new")}>
                        {t('newStation')}
                    </MDButton>
                }
                <ExpandButtonIcon {...expandButtonState} />
            </Box>
            <MDBox ref={map.mapBoxRef}
                   borderRadius="xl"
                   bgColor="grey-300"
                   sx={{
                       height: expandButtonState.mapHeight,
                       scrollMarginTop: 100,
                       overflow: "hidden",
                       mt: 2,
                       boxShadow: 1,
                       position: "relative",
                       zIndex: 2
                   }}>
                <GeoJSONMap mapRef={map.mapRef}>
                    <LayersControl position="topright">
                        <LayersControl.Overlay name="stations" checked>
                            <GeoJSONPointsCollection featureCollection={map.geoJSON}
                                                     getSelectedFeatures={map.getSelectedFeatures}
                                                     onFeatureClick={map.handleFeatureClick}
                                                     renderFeatureTooltip={renderStationTooltip}
                                                     cluster
                                                     />
                        </LayersControl.Overlay>
                    </LayersControl>
                </GeoJSONMap>
            </MDBox>
            <Card  sx={{pt: 3, mt: -3, zIndex: 1}}>
                {
                    map.selectedFeatures.length > 0 && <Box sx={{display: "flex", flexWrap: "wrap", gap: 1, pt: 3, px: 3}}>
                        {
                            map.selectedFeatures.map(f =>
                                <StationFeatureChip key={f.properties.id}
                                                    feature={f}
                                                    onDelete={() => map.toggleFeature(f)}/>
                            )
                        }
                    </Box>
                }
                <StationsTable rows={dataQuery.data?.rows}
                               page_count={dataQuery.data?.page_count}
                               filterProps={filterProps}
                               map={map}
                               showRowColoring
                               {...tableState}/>
                <ExportToolbar stationFilter={stationFilter}/>
            </Card>
        </MDBox>
    </Page>
}


export default Stations;